import { createSlice } from '@reduxjs/toolkit';

import {
  AddToReportUpdateFieldsPayload,
  ReportColor,
  ReportUpdateFieldKey,
  ReportUpdateFields
} from '@/types/transmissionReport';

import { preferencesSlice } from '../preferencesSlice';

interface TransmissionReportState {
  isAccordionSidebarOpen: boolean;
  isNotesSidebarOpen: boolean;
  activeColorCategory: ReportColor;
  activeReportId: number | null;
  fetchingNextTransmission: boolean;
  loadingMoreItemsDynamically: boolean;
  skipSelectedReportReset: boolean;
  applyingIdFromNewList: boolean;
  reportUpdateFields: ReportUpdateFields;
  addingPresentingAlert: boolean;
  isBillingLoading: boolean;
  isBillingValid: boolean;
  reportUpdateFieldsSaveStatus: 'saving' | 'failed' | 'saved' | number | null;
  signedReportsCount: number;
  isNextTransmissionMode: boolean;
  showNoReportsMessage: boolean;
  resetVendorPDFsUrls: boolean;
  search?: string;
}

const initialState: TransmissionReportState = {
  isAccordionSidebarOpen: false,
  isNotesSidebarOpen: false,
  activeColorCategory: ReportColor.Red,
  activeReportId: null,
  fetchingNextTransmission: false,
  loadingMoreItemsDynamically: false,
  skipSelectedReportReset: false,
  applyingIdFromNewList: false,
  reportUpdateFields: {},
  addingPresentingAlert: false,
  isBillingLoading: false,
  isBillingValid: false,
  reportUpdateFieldsSaveStatus: null,
  signedReportsCount: 0,
  isNextTransmissionMode: false,
  showNoReportsMessage: false,
  resetVendorPDFsUrls: false,
  search: ''
};

const transmissionReportsSlice = createSlice({
  name: 'transmissionReports',
  initialState,
  reducers: {
    setSearchQuery: (state, action: { payload: string | undefined }) => {
      state.search = action.payload;
    },

    toggleAccordionSidebar: (state) => {
      state.isAccordionSidebarOpen = !state.isAccordionSidebarOpen;
    },
    openAccordionSidebar: (state) => {
      state.isAccordionSidebarOpen = true;
    },
    closeAccordionSidebar: (state) => {
      state.isAccordionSidebarOpen = false;
    },
    toggleNotesSidebar: (state) => {
      state.isNotesSidebarOpen = !state.isNotesSidebarOpen;
    },
    openNotesSidebar: (state) => {
      state.isNotesSidebarOpen = true;
    },
    closeNotesSidebar: (state) => {
      state.isNotesSidebarOpen = false;
    },
    setActiveColorCategory: (
      state,
      action: {
        payload: { color: ReportColor; skipSelectedReportReset?: boolean };
      }
    ) => {
      state.activeColorCategory = action.payload.color;
      state.skipSelectedReportReset = action.payload.skipSelectedReportReset ?? false;
      state.reportUpdateFieldsSaveStatus = null;
    },
    resetSkipSelectedReportReset: (state) => {
      state.skipSelectedReportReset = false;
    },
    setActiveReport: (state, action: { payload: number | null }) => {
      state.activeReportId = action.payload;
      state.isNextTransmissionMode = false; // Reset nextTransmissionMode when fetching a new report
      state.reportUpdateFields = {};
      window.scrollTo({ top: 0, behavior: 'smooth' });
      state.reportUpdateFieldsSaveStatus = null;
    },
    resetActiveReport: (state) => {
      if (state.skipSelectedReportReset) {
        state.skipSelectedReportReset = false;
      } else {
        state.activeReportId = null;
      }
    },
    goToNextTransmission: (state) => {
      state.fetchingNextTransmission = true;
    },
    resetNextTransmissionToggle: (state) => {
      state.fetchingNextTransmission = false;
    },
    loadMoreItemsDynamically: (state) => {
      state.loadingMoreItemsDynamically = true;
    },
    resetLoadMoreItemsDynamicallyToggle: (state) => {
      state.loadingMoreItemsDynamically = false;
    },
    applyIdFromNewList: (state) => {
      state.applyingIdFromNewList = true;
    },
    resetApplyIdFromNewListToggle: (state) => {
      state.applyingIdFromNewList = false;
    },
    addToReportUpdateFields: <K extends ReportUpdateFieldKey>(
      state: { reportUpdateFields: { [x: string]: any } },
      action: { payload: AddToReportUpdateFieldsPayload<K> }
    ) => {
      const { key, value } = action.payload;
      state.reportUpdateFields[key] = {
        ...state.reportUpdateFields[key],
        ...value
      };
    },
    removeFromReportUpdateFields: <K extends ReportUpdateFieldKey>(
      state: { reportUpdateFields: { [x: string]: any } },
      action: { payload: { key: K; value: string } }
    ) => {
      const { key, value } = action.payload;
      if (!state.reportUpdateFields[key] || !state.reportUpdateFields[key][value]) return;
      delete state.reportUpdateFields[key][value];
      if (Object.keys(state.reportUpdateFields[key]).length === 0) {
        delete state.reportUpdateFields[key];
      }
    },
    clearReportUpdateFieldCategory: <K extends ReportUpdateFieldKey>(
      state: { reportUpdateFields: { [x: string]: any } },
      action: { payload: { key: K } }
    ) => {
      const { key } = action.payload;
      delete state.reportUpdateFields[key];
    },
    resetReportUpdateFields: (state) => {
      state.reportUpdateFields = {};
    },
    updateReportUpdateFieldsSaveStatus: (
      state,
      action: { payload: 'saving' | 'failed' | 'saved' | null }
    ) => {
      state.reportUpdateFieldsSaveStatus = action.payload;
    },
    addPresentingAlert: (state) => {
      state.addingPresentingAlert = true;
    },
    resetAddingPresentingAlert: (state) => {
      state.addingPresentingAlert = false;
    },
    setIsBillingLoading: (state, action: { payload: boolean }) => {
      state.isBillingLoading = action.payload;
    },
    setIsBillingValid: (state, action: { payload: boolean }) => {
      state.isBillingValid = action.payload;
    },
    incrementSignedReportsCount: (state) => {
      state.signedReportsCount = state.signedReportsCount + 1;
    },
    resetSignedReportsCount: (state) => {
      state.signedReportsCount = 0;
    },
    applySearchResultReport: (
      state,
      action: {
        payload: {
          reportId: number;
          color: ReportColor;
          skipSelectedReportReset?: boolean;
        };
      }
    ) => {
      state.activeReportId = action.payload.reportId;
      state.activeColorCategory = action.payload.color;
      state.skipSelectedReportReset = action.payload.skipSelectedReportReset ?? false;
      state.reportUpdateFieldsSaveStatus = null;
    },
    setIsNextTransmissionMode: (state, action: { payload: boolean }) => {
      if (state.isNextTransmissionMode !== action.payload) {
        state.isNextTransmissionMode = action.payload;
      }
    },
    setShowNoReportsMessage: (state, action: { payload: boolean }) => {
      state.showNoReportsMessage = action.payload;
    },
    setResetVendorPDFsUrls: (state, action: { payload: boolean }) => {
      state.resetVendorPDFsUrls = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(preferencesSlice.actions.setFilters, (state) => {
      state.activeColorCategory = ReportColor.Red;
      state.reportUpdateFieldsSaveStatus = null;
      state.activeReportId = null;
      state.skipSelectedReportReset = false;
    });
  }
});

export const {
  toggleAccordionSidebar,
  openAccordionSidebar,
  closeAccordionSidebar,
  toggleNotesSidebar,
  openNotesSidebar,
  closeNotesSidebar,
  setActiveColorCategory,
  resetSkipSelectedReportReset,
  setActiveReport,
  resetActiveReport,
  goToNextTransmission,
  resetNextTransmissionToggle,
  loadMoreItemsDynamically,
  resetLoadMoreItemsDynamicallyToggle,
  applyIdFromNewList,
  resetApplyIdFromNewListToggle,
  addToReportUpdateFields,
  removeFromReportUpdateFields,
  clearReportUpdateFieldCategory,
  resetReportUpdateFields,
  updateReportUpdateFieldsSaveStatus,
  addPresentingAlert,
  resetAddingPresentingAlert,
  setIsBillingLoading,
  setIsBillingValid,
  incrementSignedReportsCount,
  resetSignedReportsCount,
  applySearchResultReport,
  setIsNextTransmissionMode,
  setShowNoReportsMessage,
  setResetVendorPDFsUrls,
  setSearchQuery
} = transmissionReportsSlice.actions;
export default transmissionReportsSlice.reducer;
